import React, { ReactElement, useMemo, useState } from "react";
import { makeStyles, Button, Dialog, DialogContent, IconButton, Radio, RadioGroup, FormControlLabel, Typography, Box, useMediaQuery, Menu, MenuItem as MuiMenuItem, TextField, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CloseIcon from "@material-ui/icons/Close";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import theme from "../../../themes";
import {
  useGetAllChamberMembersQuery,
  useSendLoginInfoEmailToChamberMemberMutation,
  LoginInfoEmailTypes,
  useDeleteChamberMemberMutation,
  useUpdateChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
  useGetSampleLoginInfoEmailQuery,
} from "../../../graphql/types";
import Loader from "../../common/Loader/Loader";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import { useHistory } from "react-router-dom";
import LogoImage from "../../common/utilities/LogoImage";
import TrashIcon from "../../../images/trash.png";
import TrashIconWhite from "../../../images/trash-icon-white.svg";
import ResetPasswordBlackIcon from "../../../images/reset-password-black.png";
import ResetDashboardPasswordDialog from "../../common/ResetDashboardPasswordDialog";
import EditMemberIcon from "../../../images/edit-member.png";
import SuspendIcon from "../../../images/suspend.png";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import ExportButton from "../../common/ExportButton";
import SuccessDialog from "../../common/SuccessDialog";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import SendLoginInformationModal from "../../common/SendLoginInformationModal";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CommonTable from "../../common/CommonTable";
import { HeadCell } from "../../common/CommonTable/types";
import { EditContactForm } from "./MemberCredentialsTable";
import { useGetCategoryIdToHumanReadableName } from "../../common/Hooks/useGetCategoryIdToHumanReadableName";
import {
  buildMemberDirectoryExportRows,
  MEMBER_DIRECTORY_EXPORT_HEADERS,
} from "./memberDirectoryExport";

const useStyles = makeStyles(() => ({
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "100px",
    "@media (max-width: 1024px)": {
      padding: "24px 30px",
    },
    "@media (max-width: 768px)": {
      padding: "20px 24px",
      minHeight: "auto",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
  },
  title: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    margin: 0,
    "@media (max-width: 1024px)": {
      fontSize: "28px",
    },
    "@media (max-width: 768px)": {
      fontSize: "24px",
    },
  },
  headerIcons: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
  },
  headerIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  description: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "8px",
    lineHeight: "1.5",
    "@media (max-width: 1024px)": {
      fontSize: "16px",
    },
    "@media (max-width: 768px)": {
      fontSize: "16px",
    },
    "@media (max-width: 600px)": {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    marginTop: "20px",
    "@media (max-width: 768px)": {
      gap: "10px",
      marginTop: "16px",
    },
    "@media (max-width: 600px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "8px",
      width: "100%",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
        justifyContent: "center",
      },
    },
  },
  videoButton: {
    backgroundColor: "white",
    color: "#D4A353",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 20px",
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  exportButton: {
    backgroundColor: "white",
    color: "#D4A353",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 20px",
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  contentContainer: {
    backgroundColor: theme.palette.primary.main,
    padding: "0 20px 20px 20px",
    "@media (max-width: 1500px)": {
      padding: "0 16px 16px 16px",
    },
    "@media (max-width: 768px)": {
      padding: "0 12px 12px 12px",
    },
    "@media (max-width: 480px)": {
      padding: "0 8px 8px 8px",
    },
  },
  content: {
    background: "#FFFFFF",
    borderRadius: "16px",
    padding: "20px",
    minHeight: "70vh",
    "@media (max-width: 1500px)": {
      padding: "16px",
    },
    "@media (max-width: 768px)": {
      padding: "12px",
    },
    "@media (max-width: 480px)": {
      padding: "8px",
      borderRadius: "12px",
    },
  },
  companyName: {
    fontWeight: "bold",
    fontSize: "13px",
    color: "#000",
    "@media (max-width: 1500px)": {
      fontSize: "13px",
    },
    "@media (max-width: 1200px)": {
      fontSize: "12px",
    },
  },
  actionButton: {
    padding: "5px 10px",
    fontSize: "11px",
    textTransform: "none",
    borderRadius: "6px",
    minWidth: "auto",
    whiteSpace: "nowrap",
    fontWeight: 600,
    marginRight: "4px",
    lineHeight: "1.4",
    textDecoration: "none",
    "&:hover, &:focus, &:visited": {
      textDecoration: "none",
    },
    "@media (max-width: 1500px)": {
      padding: "4px 8px",
      fontSize: "11px",
      marginRight: "3px",
    },
    "@media (max-width: 1200px)": {
      padding: "4px 6px",
      fontSize: "10px",
      marginRight: "2px",
    },
  },
  resetButton: {
    backgroundColor: "#DEE2E6",
    color: "#333",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#CED4DA",
      color: "#333",
    },
  },
  editButton: {
    backgroundColor: "#DEE2E6",
    color: "#333",
    fontWeight: 600,
    "&:hover, &:focus, &:visited": {
      backgroundColor: "#CED4DA",
      color: "#333",
    },
    "& .MuiButton-label": {
      color: "inherit",
    },
  },
  suspendButton: {
    backgroundColor: "#DEE2E6",
    color: "#333",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#CED4DA",
    },
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#C62828",
    },
  },
  dialogPaper: {
    borderRadius: "20px",
    padding: "20px",
    maxWidth: "607px",
    position: "relative",
    fontFamily: "DM Sans, sans-serif",
  },
  dialogCloseButton: {
    position: "absolute",
    right: "10px",
    top: "10px",
    backgroundColor: "#ED1C24",
    color: "white",
    width: "30px",
    height: "30px",
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  dialogIconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
    marginTop: "20px",
  },
  dialogIcon: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogIconReset: {
    border: `3px solid ${theme.palette.primary.main}`,
  },
  dialogIconDelete: {
    border: "3px solid #EF4444",
    backgroundColor: "#FEE2E2",
  },
  dialogIconSuspend: {
    border: `3px solid ${theme.palette.primary.main}`,
  },
  dialogIconSuccess: {
    border: "3px solid #22C55E",
    backgroundColor: "#DCFCE7",
  },
  successDialogPaper: {
    borderRadius: "16px",
    padding: "20px",
    maxWidth: "900px",
    position: "relative",
  },
  successSection: {
    border: "2px dotted #E5E7EB",
    borderRadius: "12px",
    padding: "24px",
    backgroundColor: "#FEFCE8",
  },
  successSectionDark: {
    backgroundColor: "#F9FAFB",
  },
  successSectionTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "16px",
  },
  successSectionTitleGold: {
    color: theme.palette.primary.main,
  },
  successSectionTitleBlack: {
    color: "#000",
  },
  successButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  successButtonOutline: {
    backgroundColor: "white",
    color: "#000",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "none",
    fontSize: "14px",
    border: "2px solid #E5E7EB",
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  previewEmailDialogPaper: {
    borderRadius: "20px",
    padding: "0",
    width: "966px",
    maxWidth: "95vw",
    maxHeight: "95vh",
    position: "relative",
    background: "#D9D9D9",
    overflow: "hidden",
  },
  previewEmailHeader: {
    display: "flex",
    alignItems: "center",
    padding: "28px 30px 0 30px",
    marginBottom: "24px",
  },
  previewEmailTitle: {
    fontSize: "25px",
    fontWeight: 700,
    lineHeight: "200%",
    textAlign: "center",
    flex: 1,
    color: "#000000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  previewEmailContent: {
    border: "1px solid #000000",
    borderRadius: "16px",
    padding: "38px 20px",
    backgroundColor: "#FFFFFF",
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "200%",
    color: "#000000",
    margin: "0 30px 30px 30px",
    maxHeight: "calc(95vh - 150px)",
    overflowY: "auto",
  },
  backButton: {
    minWidth: "38px",
    width: "38px",
    height: "38px",
    padding: 0,
    borderRadius: "8px",
    "& svg": {
      fontSize: "24px",
      color: "#000000",
      strokeWidth: "3px",
    },
  },
  dialogTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "16px",
  },
  dialogDescription: {
    fontSize: "14px",
    color: "#666",
    textAlign: "left",
    marginBottom: "12px",
    lineHeight: "1.5",
  },
  dialogConfirmText: {
    fontSize: "14px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "24px",
    color: "#333",
  },
  dialogButtons: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
  },
  dialogButtonYes: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "12px 48px",
    textTransform: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  dialogButtonNo: {
    backgroundColor: "#333",
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "12px 48px",
    textTransform: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  dialogButtonDelete: {
    backgroundColor: "#EF4444",
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "12px 48px",
    textTransform: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#DC2626",
    },
  },
  dialogButtonCancel: {
    backgroundColor: "#333",
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "12px 48px",
    textTransform: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  editContactDialogPaper: {
    borderRadius: "24px",
    padding: "0",
    maxWidth: "800px",
    width: "100%",
    maxHeight: "95vh",
    position: "relative",
    overflow: "hidden",
    margin: "10px",
    backgroundColor: "#d9d9d9",
    [theme.breakpoints.down("sm")]: {
      borderRadius: "16px",
      margin: "12px",
      maxHeight: "calc(100vh - 24px)",
    },
  },
  editContactDialogContent: {
    padding: "16px 24px",
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    [theme.breakpoints.down("sm")]: {
      padding: "12px 14px",
    },
  },
  editContactContentArea: {
    border: "1.5px solid #4A7DB5",
    borderRadius: "16px",
    padding: "16px 20px",
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 14px",
    },
  },
  editContactSectionHeader: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "30px",
    padding: "8px 24px",
    display: "inline-block",
    position: "relative",
    top: "-18px",
    left: "0",
    marginBottom: "-10px",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 14px",
      top: "-16px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  editContactSectionTitle: {
    color: "#FFFFFF",
    fontSize: "15px",
    fontWeight: 700,
    margin: 0,
    lineHeight: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      lineHeight: "16px",
    },
  },
  editContactFormRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "20px",
    alignItems: "end",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: "14px",
      marginBottom: "14px",
    },
  },
  editContactFieldLabel: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "13px",
    color: "#000000",
    fontWeight: 600,
    marginBottom: "4px",
  },
  editContactRequiredAsterisk: {
    color: "#D32F2F",
    marginLeft: "2px",
    fontWeight: "bold",
  },
  editContactInput: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 12px",
      fontSize: "13px",
    },
  },
  editContactUpdateButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "10px 48px",
    textTransform: "none" as const,
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#D49820",
    },
  },
  editContactDialogTitle: {
    fontSize: "20px",
    fontWeight: 700,
    textAlign: "center" as const,
    color: "#000000",
    marginBottom: "12px",
  },
  backButton: {
    minWidth: "38px",
    width: "38px",
    height: "38px",
    padding: 0,
    borderRadius: "8px",
    "& svg": {
      fontSize: "24px",
      color: "#000000",
      strokeWidth: "3px",
    },
  },
  // ── Mobile card list styles ──────────────────────────────────────────
  mobileSearchBar: {
    width: "100%",
    marginBottom: "12px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#F5F5F5",
      "& fieldset": { borderColor: "#E0E0E0" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "14px",
    },
  },
  memberCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    border: "1px solid #E8E8E8",
    padding: "14px",
    marginBottom: "10px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  memberCardTop: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
  },
  memberCardInfo: {
    flex: 1,
    marginLeft: "12px",
    minWidth: 0,
  },
  memberCardName: {
    fontWeight: 700,
    fontSize: "15px",
    color: "#24272C",
    marginBottom: "5px",
    wordBreak: "break-word" as const,
  },
  memberCardStatuses: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
  },
  memberCardStatusGroup: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "baseline",
    gap: "6px",
    flexWrap: "wrap" as const,
  },
  memberCardStatusLabel: {
    fontSize: "12px",
    color: "#555",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  badge: {
    fontSize: "12px",
    fontWeight: 700,
    display: "inline-block",
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  badgeActive: {
    color: "#2E7D32",
  },
  badgeSuspended: {
    color: "#666",
  },
  badgeAccessed: {
    color: "#2E7D32",
  },
  badgeNotAccessed: {
    color: "#C62828",
  },
  memberCardActions: {
    display: "flex",
    gap: "8px",
  },
  mobileActionBtn: {
    flex: 1,
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "8px",
    textTransform: "none" as const,
    padding: "10px 8px",
    minWidth: 0,
    height: "44px",
  },
  mobileResetBtn: {
    backgroundColor: "#F3F4F6",
    color: "#374151",
    border: "1px solid #D1D5DB",
    "&:hover": { backgroundColor: "#E5E7EB" },
  },
  mobileUpdateBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    "&:hover, &:focus, &:visited": {
      backgroundColor: "#D4A353",
      color: "#FFFFFF",
    },
    "& .MuiButton-label": {
      color: "inherit",
    },
  },
  mobileActivateBtn: {
    backgroundColor: "#F0FDF4",
    color: "#15803D",
    border: "1px solid #86EFAC",
    "&:hover": { backgroundColor: "#DCFCE7" },
  },
}));

const ChamberAdminListAllChamberMembers = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [mobileSearch, setMobileSearch] = useState("");
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuMemberId, setMobileMenuMemberId] = useState<string | null>(null);
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const {
    categoryIdToHumanReadableName,
    loading: categoryDataLoading,
  } = useGetCategoryIdToHumanReadableName();
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [resetPasswordSuccessDialogOpen, setResetPasswordSuccessDialogOpen] = useState(false);
  const [previewEmailDialogOpen, setPreviewEmailDialogOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [emailSentSuccessDialogOpen, setEmailSentSuccessDialogOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [selectedMemberName, setSelectedMemberName] = useState<string>("");
  const [selectedMemberEmail, setSelectedMemberEmail] = useState<string>("");
  const [memberIsCurrentlySuspended, setMemberIsCurrentlySuspended] = useState(false);
  const [suspendDialogSelection, setSuspendDialogSelection] = useState<"active" | "suspended">("active");
  const [dialogState, setDialogState] = useState<{ open: boolean; message: string; variant: "success" | "error" }>({ open: false, message: "", variant: "success" });
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [editContactMember, setEditContactMember] = useState<any>(null);
  const [editContactSaving, setEditContactSaving] = useState(false);
  const [sendCredentialsDialogOpen, setSendCredentialsDialogOpen] = useState(false);
  const [selectedMemberHasCredentialsSent, setSelectedMemberHasCredentialsSent] = useState(false);

  const { data: chamberMemberData, loading: chamberMemberDataLoading } =
    useGetAllChamberMembersQuery({
      variables: {
        chamberId: chamberInfo?.id || "",
        getHasSignedUp: true,
        getNumberOfUsers: true,
      },
      skip: !chamberInfo?.id,
      fetchPolicy: "cache-and-network",
      fetchPolicy: "cache-and-network",
    });

  const [sendLoginInfoEmail, { loading: sendingEmail }] = useSendLoginInfoEmailToChamberMemberMutation();
  const [deleteChamberMember, { loading: deletingMember }] = useDeleteChamberMemberMutation();
  const [updateChamberMember, { loading: updatingMember }] = useUpdateChamberMemberMutation();
  const [setNewTemporaryChamberMemberPassword, { loading: resettingPassword }] = useSetNewTemporaryChamberMemberPasswordMutation();

  const { data: sampleEmailData } = useGetSampleLoginInfoEmailQuery({
    variables: {
      type: LoginInfoEmailTypes.ResetPassword,
      chamberMemberId: selectedMemberId || "",
    },
    skip: !selectedMemberId,
  });

  const handleSendEmail = async () => {
    if (!selectedMemberId || !selectedMemberEmail) {
      setDialogState({ open: true, message: enumStrings.toastMessages.missingMemberInfo, variant: "error" });
      return;
    }

    try {
      const result = await sendLoginInfoEmail({
        variables: {
          adminConsoleLoginEmail: selectedMemberEmail,
          chamberMemberId: selectedMemberId,
          type: LoginInfoEmailTypes.ResetPassword,
        },
      });

      if (result.data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully) {
        setResetPasswordSuccessDialogOpen(false);
        setEmailSentSuccessDialogOpen(true);

        // Auto-close the success dialog after 5 seconds
        setTimeout(() => {
          setEmailSentSuccessDialogOpen(false);
          setSelectedMemberId(null);
          setSelectedMemberName("");
          setSelectedMemberEmail("");
        }, 5000);
      } else {
        setDialogState({ open: true, message: enumStrings.toastMessages.failedToSendEmail, variant: "error" });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToSendEmail, variant: "error" });
    }
  };

  const tableData = useMemo(() => {
    return (chamberMemberData?.getAllChamberMembers || []).map((member) => ({
      ...member,
      // Sortable string fields for CommonTable's comparator
      statusSort: member.hasSignedUp ? "1" : "0",
      membershipStatusSort: member.isSuspended ? "1" : "0",
    }));
  }, [chamberMemberData?.getAllChamberMembers]);

  const memberDirectoryExportRows = useMemo(
    () =>
      buildMemberDirectoryExportRows({
        members: chamberMemberData?.getAllChamberMembers || [],
        categoryNameById: categoryIdToHumanReadableName,
      }),
    [
      categoryIdToHumanReadableName,
      chamberMemberData?.getAllChamberMembers,
    ]
  );

  const mobileFilteredMembers = useMemo(() => {
    const lower = mobileSearch.toLowerCase();
    if (!lower) return tableData;
    return tableData.filter((m) => m.name?.toLowerCase().includes(lower));
  }, [tableData, mobileSearch]);

  const tableColumns: HeadCell[] = useMemo(() => [
    { id: "name", label: enumStrings.manageMembersPage.companyName, width: "25%" },
    { id: "membershipStatusSort", label: enumStrings.manageMembersPage.membershipStatus, width: "15%" },
    { id: "statusSort", label: enumStrings.manageMembersPage.logInStatus, width: "15%" },
    { id: "actions", label: enumStrings.manageMembersPage.actions, width: "45%", alignCenter: true, sortable: false },
  ], [enumStrings]);

  const getEditMemberUrl = ({ memberId }: { memberId: string }) => {
    return `${RedirectUri.updateAMemberChamberAdmin}?chamberMemberId=${encodeURIComponent(memberId)}`;
  };

  const handleEditMember = ({ memberId }: { memberId: string }) => {
    history.push({
      pathname: RedirectUri.updateAMemberChamberAdmin,
      search: `?chamberMemberId=${encodeURIComponent(memberId)}`,
      state: { chamberMemberId: memberId },
    });
  };

  const handleEditMemberClick = ({
    event,
    memberId,
  }: {
    event: React.MouseEvent<HTMLAnchorElement>;
    memberId: string;
  }) => {
    event.stopPropagation();

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      return;
    }

    event.preventDefault();
    handleEditMember({ memberId });
  };

  const handleResetPassword = (memberId: string, memberName: string, memberEmail: string) => {
    setSelectedMemberId(memberId);
    setSelectedMemberName(memberName);
    setSelectedMemberEmail(memberEmail);
    setResetPasswordDialogOpen(true);
  };

  const handleSuspend = (memberId: string, memberName: string, isSuspended: boolean) => {
    setSelectedMemberId(memberId);
    setSelectedMemberName(memberName);
    setMemberIsCurrentlySuspended(isSuspended);
    setSuspendDialogSelection(isSuspended ? "suspended" : "active");
    setSuspendDialogOpen(true);
  };

  const handleDelete = (memberId: string, memberName: string) => {
    setSelectedMemberId(memberId);
    setSelectedMemberName(memberName);
    setDeleteDialogOpen(true);
  };

  const confirmResetPasswordAndSendEmail = () => {
    setResetPasswordDialogOpen(false);
    setResetPasswordSuccessDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedMemberId) return;
    try {
      await deleteChamberMember({
        variables: { id: selectedMemberId },
        refetchQueries: ["getAllChamberMembers"],
      });
      setDeleteDialogOpen(false);
      setDialogState({ open: true, message: enumStrings.toastMessages.memberDeletedSuccess, variant: "success" });
      setSelectedMemberId(null);
      setSelectedMemberName("");
    } catch (error) {
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToDeleteMember, variant: "error" });
    }
  };

  const confirmSuspend = async (overrideSelection?: "active" | "suspended") => {
    if (!selectedMemberId) return;
    const member = chamberMemberData?.getAllChamberMembers?.find((m) => m.id === selectedMemberId);
    if (!member) return;
    const selection = overrideSelection || suspendDialogSelection;
    const newSuspendedStatus = selection === "suspended";
    try {
      await updateChamberMember({
        variables: {
          id: selectedMemberId,
          input: {
            address: {
              city: member.address?.city || "",
              postalCode: member.address?.postalCode || "",
              province: member.address?.province || "",
              streetAddress: member.address?.streetAddress || "",
            },
            categoryId: member.categoryId || [],
            chamberId: member.chamberId,
            description: member.description || "",
            contactName: member.contactName || "",
            email: member.email || "",
            phoneNumber: member.phoneNumber || "",
            showInApp: member.showInApp ?? true,
            name: member.name,
            website: member.website || "",
            facebook: member.facebook || "",
            instagram: member.instagram || "",
            threads: (member as any).threads || "",
            linkedin: member.linkedin || "",
            twitter: member.twitter || "",
            tiktok: (member as any).tiktok || "",
            youtube: (member as any).youtube || "",
            googleReviewLink: (member as any).googleReviewLink || "",
            logo: member.logo || "",
            customContacts: (member.customContacts || []).map((c: any) => ({
              id: c.id,
              name: c.name || "",
              email: c.email || "",
              jobTitle: c.jobTitle || "",
              phoneNumber: c.phoneNumber || "",
            })),
            id: selectedMemberId,
            adminConsoleContactName: member.adminConsoleContactName || "",
            isSuspended: newSuspendedStatus,
          },
        },
        refetchQueries: ["getAllChamberMembers"],
      });
      setSuspendDialogOpen(false);
      setDialogState({
        open: true,
        message: newSuspendedStatus
          ? enumStrings.toastMessages.memberSuspendedSuccess
          : enumStrings.toastMessages.memberAccountActivatedSuccess,
        variant: "success",
      });
      setSelectedMemberId(null);
      setSelectedMemberName("");
      setMemberIsCurrentlySuspended(false);
    } catch (error) {
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToUpdateMembershipStatus, variant: "error" });
    }
  };

  if (chamberMemberDataLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <h1 className={classes.title}>{enumStrings.manageMembersPage.title}</h1>
        </div>
        <p className={classes.description}>
          {enumStrings.manageMembersPage.description}
        </p>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/d6873d08-2e6f-497c-b9f2-1e9a97806ccb"
          />
          <Button
            size="large"
            variant="contained"
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              lineHeight: "150%",
              backgroundColor: "#FFFFFF",
              color: theme.palette.primary.main,
              borderRadius: "8px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textTransform: "uppercase",
              padding: "10px 20px",
            }}
            onClick={() => openInstructionPdf("Manage Members.pdf")}
          >
            <InfoOutlinedIcon
              style={{
                color: theme.palette.primary.main,
                marginRight: "10px",
                width: "24px",
                height: "24px",
              }}
            />
            {enumStrings.manageMembersPage.viewInstructions}
          </Button>
          <ExportButton
            loading={chamberMemberDataLoading || categoryDataLoading}
            csvData={memberDirectoryExportRows}
            csvHeaders={MEMBER_DIRECTORY_EXPORT_HEADERS}
            label={enumStrings.ChamberAdminListAllChamberMembers.exportMembers}
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.content}>
          {isMobile ? (
            /* ── Mobile card list ── */
            <div>
              <TextField
                variant="outlined"
                placeholder={enumStrings.manageMembersPage.searchPlaceholder}
                value={mobileSearch}
                onChange={(e) => setMobileSearch(e.target.value)}
                className={classes.mobileSearchBar}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#9E9E9E", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                }}
              />
              {mobileFilteredMembers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#666" }}>
                  {enumStrings.manageMembersPage.noMembersFound}
                </div>
              ) : (
                mobileFilteredMembers.map((member) => {
                  const hasLoggedIn = member.hasSignedUp || false;
                  const isSuspended = member.isSuspended || false;
                  return (
                    <div key={member.id} className={classes.memberCard}>
                      {/* Top row: logo + name + menu */}
                      <div className={classes.memberCardTop}>
                        <LogoImage
                          logoSource={member.logo}
                          size="normal"
                          squared={true}
                          chamberName={member.name}
                        />
                        <div className={classes.memberCardInfo}>
                          <div className={classes.memberCardName}>{member.name}</div>
                        </div>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setMobileMenuAnchor(e.currentTarget); setMobileMenuMemberId(member.id); }}
                          style={{ padding: "4px", color: "#666", flexShrink: 0 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </div>
                      {/* Status rows with labels */}
                      <div className={classes.memberCardStatuses}>
                        <div className={classes.memberCardStatusGroup}>
                          <span className={classes.memberCardStatusLabel}>{enumStrings.manageMembersPage.membershipStatus}:</span>
                          <span className={`${classes.badge} ${isSuspended ? classes.badgeSuspended : classes.badgeActive}`}>
                            {isSuspended ? enumStrings.manageMembersPage.suspended : enumStrings.manageMembersPage.active}
                          </span>
                        </div>
                        <div className={classes.memberCardStatusGroup}>
                          <span className={classes.memberCardStatusLabel}>{enumStrings.manageMembersPage.logInStatus}:</span>
                          <span className={`${classes.badge} ${hasLoggedIn ? classes.badgeAccessed : classes.badgeNotAccessed}`}>
                            {hasLoggedIn ? enumStrings.manageMembersPage.loggedIn : enumStrings.manageMembersPage.neverLoggedIn}
                          </span>
                        </div>
                      </div>
                      {/* Action buttons — equal width */}
                      <div className={classes.memberCardActions} style={{ marginTop: "12px" }}>
                        <Button
                          className={`${classes.mobileActionBtn} ${classes.mobileResetBtn}`}
                          onClick={(e) => { e.stopPropagation(); handleResetPassword(member.id, member.name || "", member.adminConsoleLoginEmail || ""); }}
                          startIcon={<img src={ResetPasswordBlackIcon} alt="" style={{ width: "13px", height: "13px" }} />}
                        >
                          {enumStrings.manageMembersPage.resetPassword}
                        </Button>
                        <Button
                          component="a"
                          href={getEditMemberUrl({ memberId: member.id })}
                          className={`${classes.mobileActionBtn} ${classes.mobileUpdateBtn}`}
                          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => handleEditMemberClick({ event, memberId: member.id })}
                          startIcon={<img src={EditMemberIcon} alt="" style={{ width: "13px", height: "13px", filter: "brightness(0) invert(1)" }} />}
                        >
                          {enumStrings.manageMembersPage.editMember}
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
              {/* Three-dot menu */}
              {(() => {
                const menuMember = tableData.find((m) => m.id === mobileMenuMemberId);
                const isSusp = menuMember?.isSuspended || false;
                return (
                  <Menu
                    anchorEl={mobileMenuAnchor}
                    open={Boolean(mobileMenuAnchor) && !!menuMember}
                    onClose={() => { setMobileMenuAnchor(null); setMobileMenuMemberId(null); }}
                    PaperProps={{ style: { borderRadius: "12px", minWidth: "180px", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" } }}
                  >
                    <MuiMenuItem
                      onClick={() => { setMobileMenuAnchor(null); if (menuMember) handleSuspend(menuMember.id, menuMember.name, isSusp); }}
                      style={{ fontSize: "14px", padding: "10px 16px", gap: "10px" }}
                    >
                      <img src={SuspendIcon} alt="" style={{ width: "16px", height: "16px", filter: "brightness(0)" }} />
                      {isSusp ? enumStrings.manageMembersPage.activate : enumStrings.manageMembersPage.suspend}
                    </MuiMenuItem>
                    <MuiMenuItem
                      onClick={() => { setMobileMenuAnchor(null); if (menuMember) handleDelete(menuMember.id, menuMember.name); }}
                      style={{ fontSize: "14px", padding: "10px 16px", gap: "10px", color: "#C62828" }}
                    >
                      <img src={TrashIcon} alt="" style={{ width: "16px", height: "16px" }} />
                      {enumStrings.manageMembersPage.delete}
                    </MuiMenuItem>
                  </Menu>
                );
              })()}
            </div>
          ) : (
            /* ── Desktop table ── */
            <CommonTable
              data={tableData}
              columns={tableColumns}
              defaultSortColumn="name"
              defaultSortOrder="asc"
              searchPlaceholder={enumStrings.manageMembersPage.searchPlaceholder}
              searchFields={["name"]}
              rowKeyField="id"
              emptyMessage={enumStrings.manageMembersPage.noMembersFound}
              renderCell={(column, row) => {
                const hasLoggedIn = row.hasSignedUp || false;
                const isSuspended = row.isSuspended || false;

                switch (column.id) {
                  case "name":
                    return (
                      <Box display="flex" alignItems="center">
                        <LogoImage
                          logoSource={row.logo}
                          size="normal"
                          squared={true}
                          chamberName={row.name}
                        />
                        <Box ml={2}>
                          <span className={classes.companyName}>{row.name}</span>
                        </Box>
                      </Box>
                    );
                  case "statusSort":
                    return (
                      <span style={{ fontWeight: 700, color: hasLoggedIn ? "#2E7D32" : "#C62828" }}>
                        {hasLoggedIn ? enumStrings.manageMembersPage.loggedIn : enumStrings.manageMembersPage.neverLoggedIn}
                      </span>
                    );
                  case "membershipStatusSort":
                    return (
                      <span style={{ fontWeight: 700, color: isSuspended ? theme.palette.text.secondary : "#2E7D32" }}>
                        {isSuspended ? enumStrings.manageMembersPage.suspended : enumStrings.manageMembersPage.active}
                      </span>
                    );
                  case "actions":
                    return (
                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
                        <Button
                          className={`${classes.actionButton} ${classes.resetButton}`}
                          onClick={(e) => { e.stopPropagation(); handleResetPassword(row.id, row.name || "", row.adminConsoleLoginEmail || ""); }}
                          size="small"
                          startIcon={<img src={ResetPasswordBlackIcon} alt="Reset" style={{ width: "14px", height: "14px" }} />}
                        >
                          {enumStrings.manageMembersPage.resetPassword}
                        </Button>
                        <Button
                          component="a"
                          href={getEditMemberUrl({ memberId: row.id })}
                          className={`${classes.actionButton} ${classes.editButton}`}
                          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => handleEditMemberClick({ event, memberId: row.id })}
                          size="small"
                          startIcon={<img src={EditMemberIcon} alt="Edit" style={{ width: "14px", height: "14px" }} />}
                        >
                          {enumStrings.manageMembersPage.editMember}
                        </Button>
                        <Button
                          className={`${classes.actionButton} ${classes.suspendButton}`}
                          onClick={(e) => { e.stopPropagation(); handleSuspend(row.id, row.name, isSuspended); }}
                          size="small"
                          startIcon={<img src={SuspendIcon} alt="Suspend" style={{ width: "14px", height: "14px", filter: "brightness(0)" }} />}
                        >
                          {isSuspended ? enumStrings.manageMembersPage.activate : enumStrings.manageMembersPage.suspend}
                        </Button>
                        <Button
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                          onClick={(e) => { e.stopPropagation(); handleDelete(row.id, row.name); }}
                          size="small"
                          startIcon={<img src={TrashIcon} alt="Delete" style={{ width: "14px", height: "14px", filter: "brightness(0) invert(1)" }} />}
                        >
                          {enumStrings.manageMembersPage.delete}
                        </Button>
                      </div>
                    );
                  default:
                    return String(row[column.id] ?? "-");
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Reset Password Dialog */}
      <ResetDashboardPasswordDialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        onSendEmail={confirmResetPasswordAndSendEmail}
        onPreviewEmail={() => {
          setResetPasswordDialogOpen(false);
          setPreviewEmailDialogOpen(true);
        }}
        onEdit={() => {
          setResetPasswordDialogOpen(false);
          const member = chamberMemberData?.getAllChamberMembers?.find((m) => m.id === selectedMemberId);
          if (member) {
            setEditContactMember(member);
            setEditContactDialogOpen(true);
          }
        }}
        loading={resettingPassword || sendingEmail}
        contactName={selectedMemberName || ""}
        contactEmail={selectedMemberEmail || ""}
      />

      {/* Delete Member Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        classes={{ paper: classes.dialogPaper }}
      >
        <IconButton
          className={classes.dialogCloseButton}
          onClick={() => setDeleteDialogOpen(false)}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent style={{ padding: "20px 40px", fontFamily: "Inter, sans-serif", textAlign: "center" }}>
          <div className={classes.dialogIconContainer}>
            <div style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              backgroundColor: (theme.palette as any).delete.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <img src={TrashIconWhite} alt="Delete" style={{ width: "32px", height: "32px" }} />
            </div>
          </div>
          <div className={classes.dialogTitle} style={{ fontFamily: "Inter, sans-serif", fontSize: "22px" }}>Delete {selectedMemberName || "Member"}</div>
          <div className={classes.dialogDescription} style={{ fontFamily: "Inter, sans-serif", fontSize: "16px" }}>
            {enumStrings.manageMembersPage.deleteDialogDesc.replace("the member", selectedMemberName || "the member")}
          </div>
          <div className={classes.dialogDescription} style={{ fontStyle: "italic", fontFamily: "Inter, sans-serif", fontSize: "14px" }}>
            {enumStrings.manageMembersPage.deleteDialogPlaceholder}
          </div>
          <div className={classes.dialogButtons} style={{ marginTop: "24px", justifyContent: "center", gap: "16px" }}>
            <Button
              className={classes.dialogButtonDelete}
              onClick={confirmDelete}
              disabled={deletingMember}
              startIcon={<img src={TrashIcon} alt="" style={{ width: "18px", height: "18px", filter: "brightness(0) invert(1)" }} />}
              style={{ borderRadius: "12px", minWidth: "130px", fontSize: "14px", padding: "10px 24px" }}
            >
              {deletingMember ? "Deleting..." : enumStrings.manageMembersPage.delete}
            </Button>
            <Button
              className={classes.dialogButtonCancel}
              onClick={() => setDeleteDialogOpen(false)}
              style={{ borderRadius: "12px", minWidth: "130px", fontSize: "14px", padding: "10px 24px" }}
            >
              {enumStrings.manageMembersPage.cancel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => setSuspendDialogOpen(false)}
        classes={{ paper: classes.dialogPaper }}
      >
        <IconButton
          className={classes.dialogCloseButton}
          onClick={() => setSuspendDialogOpen(false)}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent style={{ padding: "20px 40px", fontFamily: "Inter, sans-serif" }}>
          <div className={classes.dialogIconContainer}>
            <div style={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              backgroundColor: memberIsCurrentlySuspended ? "#000000" : "#22C55E",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <PauseCircleOutlineIcon style={{ fontSize: "60px", color: "#FFFFFF" }} />
            </div>
          </div>
          <div className={classes.dialogTitle}>{enumStrings.manageMembersPage.membershipStatus}</div>
          <div className={classes.dialogDescription}>
            {memberIsCurrentlySuspended ? (
              <>
                {enumStrings.manageMembersPage.activateDialogDescription.split("\n\n").map((para, i) => (
                  <p key={i} style={{ textAlign: "center", marginBottom: "16px" }}>{para}</p>
                ))}
                <p style={{ textAlign: "center", fontWeight: 700 }}>
                  {enumStrings.manageMembersPage.areYouSureActivate}
                </p>
              </>
            ) : (
              <>
                {enumStrings.manageMembersPage.suspendDialogDescription.split("\n\n").map((para, i) => (
                  <p key={i} style={{ textAlign: "center", marginBottom: "16px" }}>{para}</p>
                ))}
                <p style={{ textAlign: "center", fontWeight: 700 }}>
                  {enumStrings.manageMembersPage.areYouSureSuspend}
                </p>
              </>
            )}
          </div>
          <div className={classes.dialogButtons} style={{ justifyContent: "center", gap: "16px", marginTop: "24px" }}>
            {memberIsCurrentlySuspended ? (
              <Button
                onClick={() => {
                  confirmSuspend("active");
                }}
                disabled={updatingMember}
                variant="contained"
                style={{
                  minWidth: "150px",
                  borderRadius: "12px",
                  backgroundColor: "#22C55E",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "14px",
                  textTransform: "uppercase",
                  boxShadow: "none",
                  padding: "10px 24px",
                }}
              >
                {updatingMember ? enumStrings.manageMembersPage.updating : enumStrings.manageMembersPage.activateButton}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  confirmSuspend("suspended");
                }}
                disabled={updatingMember}
                variant="contained"
                style={{
                  minWidth: "150px",
                  borderRadius: "12px",
                  backgroundColor: "#808080",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "14px",
                  textTransform: "uppercase",
                  boxShadow: "none",
                  padding: "10px 24px",
                }}
              >
                {updatingMember ? enumStrings.manageMembersPage.updating : enumStrings.manageMembersPage.suspendButton}
              </Button>
            )}
            <Button
              onClick={() => setSuspendDialogOpen(false)}
              disabled={updatingMember}
              variant="contained"
              style={{
                minWidth: "150px",
                borderRadius: "12px",
                backgroundColor: "#000000",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: "14px",
                textTransform: "uppercase",
                boxShadow: "none",
                padding: "10px 24px",
              }}
            >
              {enumStrings.manageMembersPage.cancelButton}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password - New Temporary Password Generated Dialog */}
      <SendLoginInformationModal
        open={resetPasswordSuccessDialogOpen}
        handleClose={() => {
          setResetPasswordSuccessDialogOpen(false);
          setSelectedMemberId(null);
          setSelectedMemberName("");
          setSelectedMemberEmail("");
        }}
        memberCompanyName={selectedMemberName}
        memberAdminPortalEmail={selectedMemberEmail}
        chamberMemberId={selectedMemberId}
        type="resetPassword"
        isSuspended={memberIsCurrentlySuspended}
      />
      <SuccessDialog
        open={dialogState.open}
        onClose={() => setDialogState(prev => ({ ...prev, open: false }))}
        message={dialogState.message}
        variant={dialogState.variant}
        autoDismissMs={5000}
      />
      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
      />

      {/* Edit Contact Dialog */}
      <Dialog
        open={editContactDialogOpen}
        onClose={() => {
          setEditContactDialogOpen(false);
          setEditContactMember(null);
        }}
        classes={{ paper: classes.editContactDialogPaper }}
        maxWidth={false}
      >
        <IconButton
          className={classes.dialogCloseButton}
          onClick={() => {
            setEditContactDialogOpen(false);
            setEditContactMember(null);
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent className={classes.editContactDialogContent}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <IconButton
              className={classes.backButton}
              onClick={() => {
                setEditContactDialogOpen(false);
                setEditContactMember(null);
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <div style={{ flex: 1 }} />
            <div style={{ width: "40px" }} />
          </div>

          <div className={classes.editContactDialogTitle}>
            {enumStrings.dialogs.editAdministratorContact}
          </div>

          <div className={classes.editContactContentArea}>
            {editContactMember && (
              <EditContactForm
                member={editContactMember}
                classes={classes}
                enumStrings={enumStrings}
                chamberInfo={chamberInfo}
                updateChamberMember={updateChamberMember}
                onClose={(updated: boolean, memberName?: string, memberEmail?: string) => {
                  if (updated && editContactMember) {
                    setSelectedMemberId(editContactMember.id);
                    setSelectedMemberName(memberName || editContactMember.adminConsoleContactName || "");
                    setSelectedMemberEmail(memberEmail || editContactMember.adminConsoleLoginEmail || "");
                    setSelectedMemberHasCredentialsSent(!!editContactMember.credentialsSentAtUtcMilli);
                    setSendCredentialsDialogOpen(true);
                  }
                  setEditContactDialogOpen(false);
                  setEditContactMember(null);
                }}
                onSaving={setEditContactSaving}
                isSaving={editContactSaving}
                showNotification={(type: "success" | "error", message: string) => {
                  setDialogState({ open: true, message, variant: type });
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Login Credentials Dialog (after edit contact) */}
      <SendLoginInformationModal
        open={sendCredentialsDialogOpen}
        handleClose={() => {
          setSendCredentialsDialogOpen(false);
          setSelectedMemberId(null);
        }}
        chamberMemberId={selectedMemberId || ""}
        memberCompanyName={selectedMemberName}
        memberAdminPortalEmail={selectedMemberEmail}
        credentialsSentAtUtcMilli={selectedMemberHasCredentialsSent ? 1 : null}
        type="memberTable"
        isSuspended={false}
      />
    </div>
  );
};

export default ChamberAdminListAllChamberMembers;
